import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Typography, message, Badge, Tag, Space, Tooltip, Empty, Select } from 'antd';
import { ReloadOutlined, CalendarOutlined, ThunderboltOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAvailableList, getUsers, flashReserve, refreshReservationData } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { TableSkeleton } from './Skeleton';

const { Title, Text } = Typography;

const AvailableList = () => {
  const navigate = useNavigate();
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserUid, setSelectedUserUid] = useState();
  const [actionLoading, setActionLoading] = useState({});

  // 场地序号映射
  const resourceNumberMap = {
    '40218': '1号',
    '38993': '2号',
  };

  const fetchAvailableTimes = async (forceRefresh = false) => {
    setLoading(true);
    try {
      if (forceRefresh) {
        const refreshResponse = await refreshReservationData();
        if (refreshResponse.status !== 'success') {
          message.error(refreshResponse.message || '刷新预约数据失败');
          return;
        }
      }

      const response = await getAvailableList();
      if (response.status === 'success') {
        const availableList = response.data || [];
        setAvailableTimes(availableList);
        if (forceRefresh) {
          message.success('预约数据已刷新');
        }
      } else {
        message.error(response.message || '获取可预约列表失败');
      }
    } catch (error) {
      console.error('获取可预约列表出错:', error);
      message.error(forceRefresh ? '刷新预约数据失败，请稍后重试' : '获取可预约列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      if (response.status === 'success') {
        const nextUsers = response.data || [];
        setUsers(nextUsers);
        const hasSelectedActiveUser = nextUsers.some((user) => user.uid === selectedUserUid && user.status !== 'disabled');
        if (!hasSelectedActiveUser) {
          setSelectedUserUid(nextUsers.find((user) => user.status !== 'disabled')?.uid);
        }
      }
    } catch (error) {
      console.error('获取用户列表失败');
    }
  };

  useEffect(() => {
    fetchAvailableTimes();
    fetchUsers();
  }, []);

  const handleFlashReserve = async (record) => {
    if (users.length === 0) {
      message.warning('请先到用户管理添加预约用户');
      navigate('/users');
      return;
    }

    const activeUsers = users.filter(u => u.status !== 'disabled');
    if (activeUsers.length === 0) {
      message.warning('没有可用用户，请先在用户管理中启用至少一个用户');
      navigate('/users');
      return;
    }

    const key = `${record.date}-${record.time}-${record.resource_id}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));

    try {
      const user = activeUsers.find((item) => item.uid === selectedUserUid);
      if (!user) {
        message.warning('请选择一个可用预约用户');
        return;
      }

      const taskData = {
        user_uid: user.uid,
        date_str: record.date,
        time_slots: [record.time],
        resource_ids: [record.resource_id],
      };

      const res = await flashReserve(taskData);
      if (res.status === 'success') {
        message.success(`⚡ 立即预约请求已提交！用户: [${user.name}]`);
        navigate('/tasks', {
          state: {
            flashReserveTaskId: res.task_id,
          },
        });
      } else {
        message.error(res.message || '预约任务创建失败');
      }
    } catch (error) {
      message.error('请求出错，请检查网络');
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const columns = [
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => {
        const dateObj = dayjs(date);
        const isToday = dateObj.isSame(dayjs(), 'day');
        
        // 中文星期映射
        const weekMap = {
          0: '周日',
          1: '周一', 
          2: '周二',
          3: '周三',
          4: '周四',
          5: '周五',
          6: '周六'
        };
        const weekDay = weekMap[dateObj.day()];
        
        return (
          <div>
            <div style={{ fontWeight: isToday ? 'bold' : 'normal' }}>
              {dateObj.format('MM-DD')}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: 'var(--text-slate-500)',
              fontWeight: isToday ? 'bold' : 'normal'
            }}>
              {weekDay}
            </div>
          </div>
        );
      }
    },
    {
      title: '时间段',
      dataIndex: 'time',
      key: 'time',
      width: 120,
      render: (time) => (
        <Tag color="blue" style={{ fontSize: '12px' }}>
          {time}
        </Tag>
      )
    },
    {
      title: '场地',
      dataIndex: 'resource_id',
      key: 'resource_id',
      width: 100,
      render: (resourceId) => {
        const fieldNumber = resourceNumberMap[resourceId] || `${resourceId}号场地`;
        return (
          <Badge 
            count={fieldNumber} 
            style={{ 
              backgroundColor: resourceId === '40218' ? '#1890ff' : '#52c41a',
              fontSize: '11px'
            }}
          />
        );
      }
    },
    {
      title: '场地名称',
      dataIndex: 'place_name',
      key: 'place_name',
      ellipsis: true,
      render: (placeName) => (
        <span style={{ fontSize: '12px' }}>
          {placeName ? placeName.replace('羽毛球场馆', '') : ''}
        </span>
      )
    },
    {
      title: '状态',
      key: 'status',
      width: 80,
      render: () => (
        <Tag color="green">可预约</Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_, record) => {
        const key = `${record.date}-${record.time}-${record.resource_id}`;
        return (
          <Space>
            <Tooltip title="以默认用户立即创建并执行预约任务">
              <Button 
                type="primary" 
                size="small" 
                icon={<ThunderboltOutlined />}
                loading={actionLoading[key]}
                onClick={() => handleFlashReserve(record)}
              >
                立即预约
              </Button>
            </Tooltip>
            <Button 
              size="small" 
              icon={<CalendarOutlined />}
              onClick={() => handleQuickReserve(record)}
            >
              配置
            </Button>
          </Space>
        );
      },
    },
  ];

  const handleQuickReserve = (record) => {
    if (!record?.date || !record?.time || !record?.resource_id) {
      message.error('预约信息不完整，请刷新后重试');
      return;
    }

    const fieldNumber = resourceNumberMap[record.resource_id] || `${record.resource_id}号场地`;
    navigate('/tasks', {
      state: {
        quickReserve: {
          date: record.date,
          time: record.time,
          resource_id: record.resource_id,
          place_name: record.place_name,
        },
      },
    });
    message.info(`已带入预约参数: ${record.date} ${record.time} ${fieldNumber}`);
  };

  return (
    <div className="available-list-page">
      <Card>
        <div className="available-list-header">
          <div className="available-list-header-left">
            <div>
              <Title level={4} style={{ margin: 0 }}>可预约场地</Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                实时空档 · 点击“配置”可创建定时预约
              </Text>
            </div>
            <Tag color={availableTimes.length ? 'green' : 'default'}>{availableTimes.length} 个空档</Tag>
          </div>
          <Space>
            <Select
              aria-label="立即预约用户"
              value={selectedUserUid}
              onChange={setSelectedUserUid}
              placeholder="选择立即预约用户"
              style={{ width: 190 }}
              options={users.filter((user) => user.status !== 'disabled').map((user) => ({
                value: user.uid,
                label: `${user.name}${user.phone ? ` · ${user.phone}` : ''}`,
              }))}
              notFoundContent="暂无可用用户"
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchAvailableTimes(true)}
              loading={loading}
            >
              强制刷新
            </Button>
            <Button
              type="primary"
              onClick={() => navigate('/tasks')}
            >
              新建预约
            </Button>
          </Space>
        </div>

        {loading ? (
          <TableSkeleton rows={8} />
        ) : availableTimes.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="暂无可预约时间，点击强制刷新获取最新数据"
          >
            <Button
              type="primary"
              onClick={() => fetchAvailableTimes(true)}
              icon={<ReloadOutlined />}
            >
              刷新数据
            </Button>
          </Empty>
        ) : (
          <Table
            dataSource={availableTimes}
            columns={columns}
            rowKey={(record) => `${record.date}-${record.time}-${record.resource_id}`}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            }}
            locale={{ emptyText: '暂无可预约时间' }}
            size="small"
            scroll={{ x: 'max-content' }}
          />
        )}
      </Card>
    </div>
  );
};

export default AvailableList; 
